import { useMemo } from 'react';
import { config } from '../../config/config';
import { ValueOf } from '../../config/constants';
import { generateChartData, generateChartDataParams } from '../../helpers';
import { AnalyticsDataBase } from '../../helpers/analytics/generateSmallCardData';

let cachedAnalytics: AnalyticsDataBase<{
    currentValue: number;
    dailyDiffPercentage: number;
    historicalData: { time: number; value: number }[];
}> | null = null;

const startValues: AnalyticsDataBase<{ baseValue: number; baseDiff: number }> = {
    dailyNewPositions: { baseValue: 150, baseDiff: 5 },
    dailyClosedPositions: { baseValue: 120, baseDiff: -3 },
    dailyUsers: { baseValue: 850, baseDiff: 12 },
    totalUsers: { baseValue: 25000, baseDiff: 45 },
    openInterests: { baseValue: 4500000, baseDiff: 250000 },
    longPositions: { baseValue: 2800000, baseDiff: 150000 },
    shortPositions: { baseValue: 1700000, baseDiff: -80000 },
    totalTrades: { baseValue: 45000, baseDiff: 1200 },
};

const generateHistoricalData = (
    timeframe: ValueOf<typeof config.areaChartTimeframes>,
    baseValue: number,
    volatility: number
) => {
    const params = generateChartDataParams(timeframe);
    const chartData = generateChartData(
        params.startTime,
        0,
        params.amountToGenerate,
        params.timeInterval
    );

    let currentValue = baseValue * 0.9;
    return chartData.map(({ time }) => {
        const change = (Math.random() * 2 - 1) * baseValue * volatility;
        currentValue = Math.max(
            baseValue * 0.7,
            Math.min(baseValue * 1.3, currentValue + change)
        );
        return {
            time,
            value: currentValue,
        };
    });
};

const useMockAnalytics = () => {
    const analytics = useMemo(() => {
        if (cachedAnalytics) return cachedAnalytics;

        cachedAnalytics = {} as any;

        (Object.keys(startValues) as (keyof typeof startValues)[]).forEach((key) => {
            const { baseValue, baseDiff } = startValues[key];
            cachedAnalytics![key] = {
                currentValue: baseValue + (Math.random() * 2 - 1) * baseDiff,
                dailyDiffPercentage: (Math.random() * 2 - 1) * 15,
                historicalData: generateHistoricalData('1d', baseValue, 0.03),
            };
        });

        return cachedAnalytics;
    }, []);

    const defaultData = useMemo(() => {
        const params = generateChartDataParams('1d');
        return generateChartData(
            params.startTime,
            0,
            params.amountToGenerate,
            params.timeInterval
        ).map(({ time, value }) => ({
            value,
            time,
        }));
    }, []);

    return {
        defaultData,
        analytics,
        isLoading: false,
        error: undefined,
    };
};

export default useMockAnalytics;
